import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxEditorModule, Editor, Toolbar } from 'ngx-editor';
import { EmailsService } from '../../../core/services/emails.service';
import { ApplicationDataService } from '../../../core/services/application-data.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { TokenStorageService } from '../../../core/services/token-storage.service';
import { AdminComplaintSubmitRequest, DraftDetail, EmailAttachment, EmailCategory } from '../../../core/models/email.model';

@Component({
  selector: 'app-email-complaint-data-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    MatInputModule,
    NgxEditorModule
  ],
  templateUrl: './email-complaint-data-view.component.html',
  styleUrls: ['./email-complaint-data-view.component.css']
})
export class EmailComplaintDataViewComponent implements OnInit, OnDestroy {

  private destroyRef = inject(DestroyRef);

  form: FormGroup;
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  draft: DraftDetail | null = null;
  emailType: EmailCategory = 'grey-listed';
  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  submitError = '';
  submitSuccess = '';
  isReadOnly = false;
  attachments: EmailAttachment[] = [];

  // Reject (greylisted → blacklist)
  showRejectModal = false;
  rejectReason = 'IRRELEVANT';
  isRejecting = false;

  // Dropdown lists
  allCategoryList: any[] = [];
  originalCategoryList: any[] = [];
  subCategoryList: any[] = [];
  originalSubCategoryList: any[] = [];
  allDivisionList: any[] = [];
  originalDivisionList: any[] = [];
  allDistrictList: any[] = [];
  districtList: any[] = [];
  originalDistrictList: any[] = [];
  allTehsilList: any[] = [];
  tehsilList: any[] = [];
  originalTehsilList: any[] = [];
  allMouzaList: any[] = [];
  mouzaList: any[] = [];
  originalMouzaList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private emailsService: EmailsService,
    private appDataService: ApplicationDataService,
    private authService: AuthenticationService,
    private tokenStorage: TokenStorageService
  ) {
    this.form = this.fb.group({
      citizenDetails: this.fb.group({
        name: ['', Validators.required],
        gender: ['', Validators.required],
        cnic: ['', Validators.required],
        phone: ['', Validators.required],
        email: [''],
        passport: [''],
        address: [''],
        keepConfidential: [false]
      }),
      complaintDetails: this.fb.group({
        complaintCategoryId: ['', Validators.required],
        complaintSubcategoryId: [''],
        daysRequired: [{ value: '', disabled: true }],
        legalFramework: [{ value: '', disabled: true }]
      }),
      locationDetails: this.fb.group({
        divisionId: [''],
        districtId: ['', Validators.required],
        tehsilId: ['', Validators.required],
        mouzaId: [''],
        complaintAddress: ['']
      }),
      description: ['']
    });
  }

  ngOnInit(): void {
    this.editor = new Editor();

    const intakeId = +this.route.snapshot.params['intakeId'];
    this.emailType = (this.route.snapshot.queryParams['type'] as EmailCategory) || 'grey-listed';

    // Set up cascade subscriptions (filter only, no child reset — this is edit mode)
    this.form.get('locationDetails.divisionId')?.valueChanges.subscribe(value => {
      this.districtList = this.allDistrictList.filter(d => d.parentAdminStrId == value);
      this.originalDistrictList = [...this.districtList];
    });
    this.form.get('locationDetails.districtId')?.valueChanges.subscribe(value => {
      this.tehsilList = this.allTehsilList.filter(d => d.parentAdminStrId == value);
      this.originalTehsilList = [...this.tehsilList];
    });
    this.form.get('locationDetails.tehsilId')?.valueChanges.subscribe(value => {
      this.mouzaList = this.allMouzaList.filter(d => d.parentAdminStrId == value);
      this.originalMouzaList = [...this.mouzaList];
    });
    this.form.get('complaintDetails.complaintCategoryId')?.valueChanges.subscribe(value => {
      if (value) {
        this.appDataService.getAllComplaintByParentCategory(value).subscribe((res: any) => {
          this.subCategoryList = res?.data || [];
          this.originalSubCategoryList = [...this.subCategoryList];
        });
      } else {
        this.subCategoryList = [];
      }
    });

    if (intakeId) {
      this.loadData(intakeId);
    }
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  // ── Data Loading ──────────────────────────────────────────────────────────

  private loadData(intakeId: number) {
    this.isLoading = true;
    this.errorMessage = '';

    forkJoin({
      draft: this.emailsService.getDraftDetail(intakeId),
      categories: this.appDataService.getAllComplaintCategoriesBySegment(this.authService.getUserSegment()),
      divisions: this.appDataService.getAdminStructureByIds(this.tokenStorage.getDivisions()),
      districts: this.appDataService.getAdminStructureByIds(this.tokenStorage.getDistricts()),
      tehsils: this.appDataService.getAdminStructureByIds(this.tokenStorage.getTehsil()),
      mouzas: this.appDataService.getAdminStructureByIds(this.tokenStorage.getMouza()),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ draft, categories, divisions, districts, tehsils, mouzas }: any) => {
        this.allCategoryList = categories?.data || [];
        this.originalCategoryList = [...this.allCategoryList];
        this.allDivisionList = divisions?.data || [];
        this.originalDivisionList = [...this.allDivisionList];
        this.allDistrictList = districts?.data || [];
        this.allTehsilList = tehsils?.data || [];
        this.allMouzaList = mouzas?.data || [];

        this.draft = draft;
        this.populateForm(draft);
        this.loadAttachments(draft.intakeId);
        this.isLoading = false;

        if (this.emailType === 'white-listed') {
          this.isReadOnly = true;
          this.form.disable();
        }

        // Load sub-categories non-blocking — form is already displayed
        if (draft.complaintCategoryId) {
          this.appDataService.getAllComplaintByParentCategory(draft.complaintCategoryId)
            .subscribe((res: any) => {
              this.subCategoryList = res?.data || [];
              this.originalSubCategoryList = [...this.subCategoryList];
            });
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load complaint data.';
      }
    });
  }

  /** Non-blocking — the attachments section just stays empty if this fails. */
  private loadAttachments(intakeId: number) {
    this.emailsService.getAttachments(intakeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (files) => this.attachments = files || [],
        error: () => this.attachments = []
      });
  }

  private populateForm(draft: DraftDetail) {
    // Pre-build filtered lists based on draft IDs before patching so dropdowns show correctly
    if (draft.divisionId) {
      this.districtList = this.allDistrictList.filter(d => d.parentAdminStrId == draft.divisionId);
      this.originalDistrictList = [...this.districtList];
    }
    if (draft.districtId) {
      this.tehsilList = this.allTehsilList.filter(d => d.parentAdminStrId == draft.districtId);
      this.originalTehsilList = [...this.tehsilList];
    }
    if (draft.tehsilId) {
      this.mouzaList = this.allMouzaList.filter(d => d.parentAdminStrId == draft.tehsilId);
      this.originalMouzaList = [...this.mouzaList];
    }

    // Patch without emitting so cascade subscriptions don't fire during initial load
    this.form.patchValue({
      citizenDetails: {
        name: draft.name || '',
        gender: draft.gender || '',
        cnic: draft.cnic || '',
        phone: draft.mobile || '',
        email: draft.senderEmail || '',
        address: draft.address || ''
      },
      complaintDetails: {
        complaintCategoryId: draft.complaintCategoryId || '',
        complaintSubcategoryId: draft.subCategoryId || ''
      },
      locationDetails: {
        divisionId: draft.divisionId || '',
        districtId: draft.districtId || '',
        tehsilId: draft.tehsilId || '',
        mouzaId: draft.mouzaId || '',
        complaintAddress: draft.complaintAddress || ''
      },
      description: draft.body || draft.complaintDetail || ''
    }, { emitEvent: false });
  }

  // ── Dropdown Filters ──────────────────────────────────────────────────────

  filterCategory(event: any) {
    const term = event.target.value.toLowerCase();
    this.allCategoryList = term
      ? this.originalCategoryList.filter(c => c.name.toLowerCase().includes(term))
      : [...this.originalCategoryList];
  }

  filterSubCategory(event: any) {
    const term = event.target.value.toLowerCase();
    this.subCategoryList = term
      ? this.originalSubCategoryList.filter(c => c.name.toLowerCase().includes(term))
      : [...this.originalSubCategoryList];
  }

  filterDivisions(event: any) {
    const term = event.target.value.toLowerCase();
    this.allDivisionList = term
      ? this.originalDivisionList.filter(d => d.name.toLowerCase().includes(term))
      : [...this.originalDivisionList];
  }

  filterDistricts(event: any) {
    const term = event.target.value.toLowerCase();
    this.districtList = term
      ? this.originalDistrictList.filter(d => d.name.toLowerCase().includes(term))
      : [...this.originalDistrictList];
  }

  filterTehsil(event: any) {
    const term = event.target.value.toLowerCase();
    this.tehsilList = term
      ? this.originalTehsilList.filter(d => d.name.toLowerCase().includes(term))
      : [...this.originalTehsilList];
  }

  filterMouza(event: any) {
    const term = event.target.value.toLowerCase();
    this.mouzaList = term
      ? this.originalMouzaList.filter(d => d.name.toLowerCase().includes(term))
      : [...this.originalMouzaList];
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getAttachmentIcon(mimeType: string): string {
    const mime = (mimeType || '').toLowerCase();
    if (mime.startsWith('image/'))  return 'image';
    if (mime.includes('pdf'))       return 'picture_as_pdf';
    return 'description';
  }

  formatAttachmentSize(bytes: number | null): string {
    if (!bytes || bytes <= 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  downloadAttachment(file: EmailAttachment) {
    const link = document.createElement('a');
    link.href = this.emailsService.getAttachmentDownloadUrl(file.attachmentId);
    link.download = file.fileName;
    link.click();
  }

  getMissingFieldsList(): string[] {
    if (!this.draft?.missingFields) return [];
    return this.draft.missingFields.split(',').map(f => f.trim()).filter(f => f);
  }

  get headerTitle(): string {
    if (this.draft?.complaintNo) return `Complaint Review: ${this.draft.complaintNo}`;
    if (this.draft?.draftId) return `Complaint Review: Draft #${this.draft.draftId}`;
    return 'Complaint Review';
  }

  get f() { return this.form.controls; }

  // ── Actions ───────────────────────────────────────────────────────────────

  goBack() {
    this.location.back();
  }

  onApprove() {
    if (!this.draft || this.isSubmitting) return;

    this.form.markAllAsTouched();
    this.submitError = '';
    this.submitSuccess = '';

    if (this.form.invalid) {
      this.submitError = 'Please fill in all required fields before submitting.';
      return;
    }

    const fv = this.form.getRawValue();

    const request: AdminComplaintSubmitRequest = {
      name: fv.citizenDetails.name || null,
      cnic: fv.citizenDetails.cnic || null,
      mobile: fv.citizenDetails.phone || null,
      gender: fv.citizenDetails.gender || null,
      address: fv.citizenDetails.address || null,
      divisionId: fv.locationDetails.divisionId || null,
      districtId: fv.locationDetails.districtId || null,
      tehsilId: fv.locationDetails.tehsilId || null,
      mouzaId: fv.locationDetails.mouzaId || null,
      districtName: this.allDistrictList.find(d => d.adminStrId == fv.locationDetails.districtId)?.name || null,
      tehsilName: this.allTehsilList.find(d => d.adminStrId == fv.locationDetails.tehsilId)?.name || null,
      mouzaName: this.allMouzaList.find(d => d.adminStrId == fv.locationDetails.mouzaId)?.name || null,
      complaintAddress: fv.locationDetails.complaintAddress || null,
      complaintCategoryId: fv.complaintDetails.complaintCategoryId || null,
      complaintCategoryName: this.allCategoryList.find(c => c.complaintCategoryId == fv.complaintDetails.complaintCategoryId)?.name || null,
      subCategoryId: fv.complaintDetails.complaintSubcategoryId || null,
      subCategoryName: this.subCategoryList.find(c => c.complaintCategoryId == fv.complaintDetails.complaintSubcategoryId)?.name || null,
      complaintDetail: fv.description || null,
    };

    this.isSubmitting = true;

    this.emailsService.submitDraft(this.draft.draftId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response.registrationStatus === 'REGISTERED') {
            this.submitSuccess = `Complaint registered: ${response.complaintNo}`;
            this.draft!.registrationStatus = 'REGISTERED';
            this.draft!.complaintNo = response.complaintNo;
            this.draft!.complaintId = response.complaintId;
            this.isReadOnly = true;
            this.form.disable();
          } else {
            this.submitError = response.message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = 'Failed to submit complaint. Please try again.';
        }
      });
  }

  onReject() {
    if (!this.draft || this.isSubmitting || this.isRejecting) return;
    this.rejectReason = 'IRRELEVANT';
    this.showRejectModal = true;
  }

  confirmReject() {
    if (!this.draft || this.isRejecting) return;

    this.isRejecting = true;
    this.submitError = '';
    this.submitSuccess = '';

    this.emailsService.rejectIntake(this.draft.intakeId, this.rejectReason)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isRejecting = false;
          this.showRejectModal = false;
          this.submitSuccess = response.message || 'Email moved to black list.';
          this.isReadOnly = true;
          this.form.disable();
        },
        error: (err) => {
          this.isRejecting = false;
          this.showRejectModal = false;
          this.submitError = err?.error?.message || 'Failed to reject email. Please try again.';
        }
      });
  }
}
